import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import Checkbox from "src/components/Checkbox/Checkbox";

import { ChartCard, EmptyState, KpiCard, KpiRow } from "./MetricsCards";
import {
  AXIS_PROPS,
  CHART_COLORS,
  CHART_LINE,
  CHART_TEXT,
  LEGEND_PROPS,
  TOOLTIP_PROPS,
  downloadCsv,
  formatMonth,
  formatNumber,
  topWithOther,
} from "./metrics.utils";

import styles from "./CompanyMetricsTab.module.scss";

interface IMonthCount {
  month: string;
  count: number;
  cumulative?: number;
  breakdown?: Record<string, number>;
}

interface ICount {
  key: string;
  count: number;
}

export interface IGrowthData {
  totals: { events: number; shows: number; users: number; dogs: number };
  previous: { events: number; shows: number; users: number; dogs: number };
  events_by_month: IMonthCount[];
  events_by_club: ICount[];
  shows_by_type: ICount[];
  users_by_month: IMonthCount[];
  dogs_by_month: IMonthCount[];
}

const CategoryTick: React.FC<any> = ({ x, y, payload, width }) => (
  <Text x={x} y={y + 4} width={width} textAnchor="middle" verticalAnchor="start" fontSize={12} fontFamily="Roboto" fill={CHART_TEXT}>
    {payload.value}
  </Text>
);

const ColumnBars: React.FC<{ rows: ICount[]; color: string }> = ({ rows, color }) => {
  if (!rows.length) return <EmptyState />;
  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={rows} margin={{ top: 10, right: 10, bottom: 10 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="key"
          interval={0}
          height={50}
          axisLine={AXIS_PROPS.axisLine}
          tickLine={false}
          tick={(tickProps: any) => {
            const slot = Number(tickProps.width) / rows.length - 8;
            return <CategoryTick {...tickProps} width={Number.isFinite(slot) ? Math.max(60, slot) : 80} />;
          }}
        />
        <YAxis {...AXIS_PROPS} allowDecimals={false} />
        <Tooltip {...TOOLTIP_PROPS} formatter={(v: number) => formatNumber(v)} />
        <Bar dataKey="count" name="Count" fill={color} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const MonthlyWithTotal: React.FC<{ rows: IMonthCount[]; label: string; color: string }> = ({ rows, label, color }) => {
  const data = rows.map((r) => ({ ...r, label: formatMonth(r.month) }));
  return (
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" {...AXIS_PROPS} />
        <YAxis {...AXIS_PROPS} yAxisId="left" allowDecimals={false} />
        <YAxis {...AXIS_PROPS} yAxisId="right" orientation="right" allowDecimals={false} />
        <Tooltip {...TOOLTIP_PROPS} formatter={(v: number) => formatNumber(v)} />
        <Legend {...LEGEND_PROPS} />
        <Bar yAxisId="left" dataKey="count" name={`New ${label}`} fill={color} radius={[4, 4, 0, 0]} />
        <Line yAxisId="right" type="monotone" dataKey="cumulative" name={`Total ${label}`} stroke={CHART_LINE} dot={false} strokeWidth={2} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const GrowthPanel: React.FC<{ data: IGrowthData }> = ({ data }) => {
  const [splitByClub, setSplitByClub] = useState<boolean>(false);

  const clubs = useMemo(() => data.events_by_club.map((c) => c.key), [data.events_by_club]);
  const eventsByMonth = useMemo(
    () => data.events_by_month.map((r) => ({ label: formatMonth(r.month), count: r.count, ...(r.breakdown || {}) })),
    [data.events_by_month]
  );

  return (
    <div>
      <KpiRow>
        <KpiCard label="Events" value={formatNumber(data.totals.events)} current={data.totals.events} previous={data.previous.events} />
        <KpiCard label="Shows / trials" value={formatNumber(data.totals.shows)} current={data.totals.shows} previous={data.previous.shows} />
        <KpiCard label="New users" value={formatNumber(data.totals.users)} current={data.totals.users} previous={data.previous.users} />
        <KpiCard label="New dogs" value={formatNumber(data.totals.dogs)} current={data.totals.dogs} previous={data.previous.dogs} />
      </KpiRow>

      <div className={styles.grid}>
        <ChartCard
          wide
          title="Events per month"
          subtitle="Published and draft events by start date"
          onExport={() => downloadCsv("events-per-month", data.events_by_month.map((r) => ({ month: r.month, events: r.count, ...(r.breakdown || {}) })))}
        >
          <Checkbox
            id="metrics-split-by-club"
            className={styles.toggle}
            label="Split by sanctioning club"
            value={splitByClub}
            onChange={(e) => setSplitByClub(e.target.checked)}
          />
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={eventsByMonth}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} allowDecimals={false} />
              <Tooltip {...TOOLTIP_PROPS} />
              {splitByClub ? (
                clubs.map((club, i) => <Bar key={club} dataKey={club} stackId="clubs" fill={CHART_COLORS[i % CHART_COLORS.length]} />)
              ) : (
                <Bar dataKey="count" name="Events" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
              )}
              {splitByClub && <Legend {...LEGEND_PROPS} />}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          wide
          title="Events by sanctioning club"
          onExport={() => downloadCsv("events-by-sanctioning-club", data.events_by_club.map((r) => ({ sanctioning_club: r.key, events: r.count })))}
        >
          <ColumnBars rows={data.events_by_club} color={CHART_COLORS[1]} />
        </ChartCard>

        <ChartCard
          wide
          title="Shows by show type"
          subtitle="Top 15 show types"
          onExport={() => downloadCsv("shows-by-type", data.shows_by_type.map((r) => ({ show_type: r.key, shows: r.count })))}
        >
          <ColumnBars rows={topWithOther(data.shows_by_type, 15)} color={CHART_COLORS[2]} />
        </ChartCard>

        <ChartCard
          title="Users by month created"
          onExport={() => downloadCsv("users-by-month", data.users_by_month.map((r) => ({ month: r.month, new_users: r.count, total_users: r.cumulative })))}
        >
          <MonthlyWithTotal rows={data.users_by_month} label="users" color={CHART_COLORS[3]} />
        </ChartCard>

        <ChartCard
          title="Dogs by month created"
          onExport={() => downloadCsv("dogs-by-month", data.dogs_by_month.map((r) => ({ month: r.month, new_dogs: r.count, total_dogs: r.cumulative })))}
        >
          <MonthlyWithTotal rows={data.dogs_by_month} label="dogs" color={CHART_COLORS[4]} />
        </ChartCard>
      </div>
    </div>
  );
};

export default GrowthPanel;
